import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { fetchWithAuth } from "../Components/FetchWithAuth";
import { url } from "../Components/url";
import NotificationToast from "../Components/notifications/NotificationToast";
import { socket } from "../socket";
import { acquireSocketConnection, releaseSocketConnection } from "../socketLifecycle";
import {
  subscribeToPush,
  sendSubscriptionToBackend,
} from "../utils/pushSubscription";
import { isPublicAnonymousRoute } from "../utils/publicAuthPaths";

const NotificationContext = createContext(null);

const TOAST_DURATION_MS = 5000;

/** Temporarily off: no Socket.IO for live notification / badge updates (REST + bell dropdown still work). Set to true to re-enable. */
const NOTIFICATION_WEBSOCKET_ENABLED = false;

function getApiBase() {
  return (url || "").replace(/\/+$/, "");
}

function toRelativeTime(isoString) {
  if (!isoString) return "just now";
  let normalizedIso = String(isoString).trim();
  // Treat timestamps without timezone as UTC to avoid client-local offset drift.
  if (
    normalizedIso &&
    !/[zZ]$/.test(normalizedIso) &&
    !/[+-]\d{2}:?\d{2}$/.test(normalizedIso)
  ) {
    normalizedIso = normalizedIso.replace(" ", "T");
    normalizedIso = `${normalizedIso}Z`;
  }
  const createdAt = new Date(normalizedIso).getTime();
  if (Number.isNaN(createdAt)) return "just now";
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - createdAt) / 1000));

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    return `${m} min${m === 1 ? "" : "s"} ago`;
  }
  if (diffSec < 86400) {
    const h = Math.floor(diffSec / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  const d = Math.floor(diffSec / 86400);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function normalizeNotification(notification) {
  return {
    id: notification.id,
    title: notification.title || "",
    message: notification.message || "",
    type: notification.type || "info",
    is_read: Boolean(notification.is_read),
    created_at: notification.created_at,
    link: notification.link || null,
    metadata: notification.metadata || null,
  };
}

export function NotificationProvider({ children }) {
  const apiBase = useMemo(() => getApiBase(), []);
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);

  useEffect(() => {
    if (!window.__notificationHistoryPatched) {
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = function patchedPushState(...args) {
        const result = originalPushState.apply(this, args);
        window.dispatchEvent(new Event("locationchange"));
        return result;
      };

      window.history.replaceState = function patchedReplaceState(...args) {
        const result = originalReplaceState.apply(this, args);
        window.dispatchEvent(new Event("locationchange"));
        return result;
      };

      window.__notificationHistoryPatched = true;
    }

    const syncPath = () => setCurrentPath(window.location.pathname);
    window.addEventListener("popstate", syncPath);
    window.addEventListener("hashchange", syncPath);
    window.addEventListener("locationchange", syncPath);
    return () => {
      window.removeEventListener("popstate", syncPath);
      window.removeEventListener("hashchange", syncPath);
      window.removeEventListener("locationchange", syncPath);
    };
  }, []);

  const isAuthenticatedRoute = useMemo(
    () => !isPublicAnonymousRoute(currentPath),
    [currentPath]
  );

  const hasSessionUser = useMemo(() => {
    const username = localStorage.getItem("username");
    return Boolean(username && username.trim());
  }, [currentPath]);

  const notificationsEnabled = isAuthenticatedRoute && hasSessionUser;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toasts, setToasts] = useState([]);
  const seenNotificationIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const addNotification = useCallback((notification) => {
    const normalized = normalizeNotification(notification);
    setNotifications((prev) => [normalized, ...prev]);
    if (!normalized.is_read) {
      setUnreadCount((prev) => prev + 1);
    }
    setToasts((prev) => [
      ...prev,
      {
        ...normalized,
        toastId: `${normalized.id || "new"}-${Date.now()}-${Math.random()}`,
      },
    ]);
    if (normalized.id) {
      seenNotificationIdsRef.current.add(normalized.id);
    }
  }, []);

  const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  const pushSupported =
    Boolean(vapidKey) &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator;

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushEnabling, setPushEnabling] = useState(false);
  const [pushError, setPushError] = useState(null);

  const checkPushSubscription = useCallback(async () => {
    if (!pushSupported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = reg?.pushManager ? await reg.pushManager.getSubscription() : null;
      setPushEnabled(!!sub);
    } catch {
      setPushEnabled(false);
    }
  }, [pushSupported]);

  useEffect(() => {
    if (notificationsEnabled && pushSupported) checkPushSubscription();
  }, [notificationsEnabled, pushSupported, checkPushSubscription]);

  const enablePushNotifications = useCallback(async () => {
    if (!pushSupported || !vapidKey) {
      setPushError("Push notifications are not configured.");
      return;
    }
    setPushEnabling(true);
    setPushError(null);
    try {
      const sub = await subscribeToPush(apiBase, vapidKey);
      if (!sub) {
        setPushError("Permission denied or unavailable.");
        return;
      }
      await sendSubscriptionToBackend(apiBase, sub);
      setPushEnabled(true);
    } catch (err) {
      setPushError(err?.message || "Failed to enable push notifications.");
    } finally {
      setPushEnabling(false);
    }
  }, [apiBase, vapidKey, pushSupported]);

  const fetchNotifications = useCallback(
    async ({ page = 1, perPage = 20, unreadOnly = false, silent = false } = {}) => {
      if (!notificationsEnabled) return null;
      if (!silent) setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(perPage),
          unread_only: unreadOnly ? "true" : "false",
        });
        const response = await fetchWithAuth(`${apiBase}/notifications?${params.toString()}`, {
          method: "GET",
        });
        if (!response) {
          return null;
        }

        const data = await response.json();
        const incoming = Array.isArray(data.notifications)
          ? data.notifications.map(normalizeNotification)
          : [];

        setNotifications(incoming);
        setUnreadCount(Number(data.unread_count || 0));

        if (!initializedRef.current) {
          incoming.forEach((n) => seenNotificationIdsRef.current.add(n.id));
          initializedRef.current = true;
        } else {
          const freshUnread = incoming.filter(
            (n) => !n.is_read && !seenNotificationIdsRef.current.has(n.id)
          );
          if (freshUnread.length > 0) {
            setToasts((prev) => {
              const next = [...prev];
              freshUnread.forEach((item) => {
                next.push({
                  ...item,
                  toastId: `${item.id}-${Date.now()}-${Math.random()}`,
                });
              });
              return next;
            });
          }
          incoming.forEach((n) => seenNotificationIdsRef.current.add(n.id));
        }

        return data;
      } catch (err) {
        setError(err.message || "Failed to fetch notifications");
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiBase, notificationsEnabled]
  );

  const removeToast = useCallback((toastId) => {
    setToasts((prev) => prev.filter((t) => t.toastId !== toastId));
  }, []);

  const markRead = useCallback(
    async (notificationId) => {
      if (!notificationsEnabled) return;
      const target = notifications.find((n) => n.id === notificationId);
      const wasUnread = target ? !target.is_read : false;

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? {
                ...item,
                is_read: true,
              }
            : item
        )
      );
      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      try {
        await fetchWithAuth(`${apiBase}/notifications/mark-read/${notificationId}`, {
          method: "POST",
        });
      } catch (err) {
        setError(err.message || "Failed to mark notification as read");
        if (wasUnread) {
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === notificationId
                ? {
                    ...item,
                    is_read: false,
                  }
                : item
            )
          );
          setUnreadCount((prev) => prev + 1);
        }
      }
    },
    [apiBase, notifications, notificationsEnabled]
  );

  const markAllRead = useCallback(async () => {
    if (!notificationsEnabled) return;
    const hadUnread = notifications.some((item) => !item.is_read);
    const prevNotifications = notifications;

    setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
    setUnreadCount(0);

    try {
      await fetchWithAuth(`${apiBase}/notifications/mark-all-read`, {
        method: "POST",
      });
    } catch (err) {
      setError(err.message || "Failed to mark all notifications as read");
      if (hadUnread) {
        setNotifications(prevNotifications);
        setUnreadCount(prevNotifications.filter((item) => !item.is_read).length);
      }
    }
  }, [apiBase, notifications, notificationsEnabled]);

  const openNotification = useCallback(
    async (notification) => {
      if (!notification.is_read) {
        await markRead(notification.id);
      }
      if (notification.link) {
        let nextLink = notification.link;
        try {
          const isEnquiryProfile = /^\/enquiry_profile(?:\?|$)/.test(nextLink);
          const enquiryId = notification?.metadata?.enquiry_id || notification?.metadata?.enquiryId;
          if (isEnquiryProfile && enquiryId && !/[?&](id|enquiryId)=/.test(nextLink)) {
            const separator = nextLink.includes("?") ? "&" : "?";
            nextLink = `${nextLink}${separator}id=${encodeURIComponent(enquiryId)}`;
          }
        } catch {
          nextLink = notification.link;
        }
        window.history.pushState({}, "", nextLink);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    },
    [markRead]
  );

  useEffect(() => {
    if (!notificationsEnabled) {
      setNotifications([]);
      setUnreadCount(0);
      setToasts([]);
      setError("");
      setLoading(false);
      seenNotificationIdsRef.current = new Set();
      initializedRef.current = false;
      return undefined;
    }

    if (!NOTIFICATION_WEBSOCKET_ENABLED) {
      return undefined;
    }

    const ownerKey = "notifications";
    acquireSocketConnection(ownerKey);

    const onNotification = (payload) => {
      if (payload) addNotification(payload);
    };
    const onNotificationCount = (payload) => {
      if (payload && typeof payload.unread_count === "number") {
        setUnreadCount(payload.unread_count);
      }
    };

    socket.on("notification", onNotification);
    socket.on("notification_count", onNotificationCount);

    return () => {
      socket.off("notification", onNotification);
      socket.off("notification_count", onNotificationCount);
      releaseSocketConnection(ownerKey);
    };
  }, [notificationsEnabled, addNotification]);

  useEffect(() => {
    if (!notificationsEnabled) return;
    fetchNotifications();
  }, [notificationsEnabled, fetchNotifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      error,
      fetchNotifications,
      markRead,
      markAllRead,
      addNotification,
      openNotification,
      toRelativeTime,
      pushSupported,
      pushEnabled,
      pushEnabling,
      pushError,
      enablePushNotifications,
      checkPushSubscription,
    }),
    [
      notifications,
      unreadCount,
      loading,
      error,
      fetchNotifications,
      markRead,
      markAllRead,
      addNotification,
      openNotification,
      pushSupported,
      pushEnabled,
      pushEnabling,
      pushError,
      enablePushNotifications,
      checkPushSubscription,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationToast
        toasts={toasts}
        durationMs={TOAST_DURATION_MS}
        onDismiss={removeToast}
        onOpen={openNotification}
      />
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
}

